import { useState, useRef, useImperativeHandle, useEffect } from "react";
import { useTranslate } from "../../../features/translation/hooks/use-translation";
import getValidator from "../services/validate-input";

const useCheckbox = (props) => {
  const { t } = useTranslate();
  const validateInput = getValidator(t);
  const ref = useRef();

  const [values, setValues] = useState(props.defaultValue || []);
  const [disabled, setDisabled] = useState(props.disabled || false);
  const [error, setError] = useState(validateInput(props, props.defaultValue || []));
  const [displayError, setDisplayError] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const validate = (newList = values) => {
    const err = validateInput(props, newList);
    setError(err);
    return err;
  };

  useEffect(() => {
    if (hasInteracted) {
      setDisplayError(!!error);
    } else {
      setDisplayError(false);
    }
  }, [error, hasInteracted]);

  const onChange = (e) => {
    const { value, checked } = e.target;
    let newList = checked
      ? [...values, value]
      : values.filter((v) => v !== value);

    setValues(newList);
    const err = validate(newList);

    if (!hasInteracted) setHasInteracted(true);

    ref.current.value = newList;

    if (props.onChange) {
      props.onChange(e);
    }
  };

  const onBlur = () => {
    if (!hasInteracted) setHasInteracted(true);
    if (error) setDisplayError(true);
  };

  useEffect(() => {
    validate(values);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        const resetValues = props.defaultValue || [];
        setValues(resetValues);
        ref.current.value = resetValues;
        validate(resetValues);
        setHasInteracted(false);
        setDisplayError(false);
        props.onReset?.();
      },
      validate: () => validate(values),
      setValue: (value) => {
        setValues(value);
        validate(value);
        setHasInteracted(true);
      },
      getId: () => props.id,
      value: values,
      name: props.name,
      id: props.id,
      getName: () => props.name,
      isValid: () => !error,
      setDisabled: (value) => setDisabled(value),
      setDisplayError: (value) => setDisplayError(value),
    }),
    [error, values]
  );

  return {
    ref,
    bind: {
      add: (val) => {
        let newList = [...new Set([...values, ...(Array.isArray(val) ? val : [val])])];
        setValues(newList);
        validate(newList);
        ref.current.value = newList;
        if (!hasInteracted) setHasInteracted(true);
        props.onAdd?.(val);
      },
      remove: (val) => {
        let removeSet = new Set(Array.isArray(val) ? val : [val]);
        let newList = values.filter((v) => !removeSet.has(v));
        setValues(newList);
        validate(newList);
        ref.current.value = newList;
        if (!hasInteracted) setHasInteracted(true);
        props.onRemove?.(val);
      },
      values,
      reset: () => {
        const resetValues = props.defaultValue || [];
        setValues(resetValues);
        ref.current.value = resetValues;
        validate(resetValues);
        setHasInteracted(false);
        setDisplayError(false);
        props.onReset?.();
      },
      onChange,
      onBlur,
      update: (value) => {
        setValues(value);
        validate(value);
        if (!hasInteracted) setHasInteracted(true);
        ref.current.value = value;
      },
      disabled,
      error,
      displayError,
    },
  };
};

export default useCheckbox;

